import {
	faCalendarDays as farCalendarDays,
	faCalendarPlus as farCalendarPlus,
	faCircleDot as farCircleDot,
	faCreditCard as farCreditCard,
	faLock as farLock,
	faMoneyBill1 as farMoneyBill1,
} from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faChild as fasChild,
	faCircleExclamation as fasCircleExclamation,
	faCoins as fasCoins,
	faHouse as fasHouse,
	faLocationDot as fasLocationDot,
	faShuffle as fasShuffle,
	faStar as fasStar,
	faUsers as fasUsers,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type {
	AbstractSearchFormProps,
	AbstractSearchFormState,
} from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { AbstractSearchForm } from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText, sprintf } from "@ui/format";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import {
	Alert,
	CollapseBlock,
	FormCheck,
	FormCheckList,
	FormCurrency,
	FormDate,
	FormNumber,
	FormRadio,
	FormSelect,
	__,
} from "bm-ui-kit";
import type { FormCheckOptions } from "bm-ui-kit";
import dayjs from "dayjs";
import type { FormApi } from "final-form";
import compact from "lodash/compact";
import React from "react";
import type { ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import {
	CONFIG,
	CSOK_LOAN_GOALS,
	CSOK_PLUS_LOAN_GOALS,
	GREEN_LOAN_GOALS,
	LOAN_GOALS,
	SOCIAL_DISCOUNT_LOAN_GOALS,
} from "./data";
import { getRegularBanks, isCSOKVariant } from "./domain";
import type {
	LoanGoal,
	LoanGoalOption,
	MortgageListingCalculatorVariant,
	MortgageSearchParams,
	MortgageSort,
} from "./types";

interface Props extends AbstractSearchFormProps<MortgageSearchParams> {
	variant?: MortgageListingCalculatorVariant;
	multipleInterestPeriods?: boolean;
	sort?: MortgageSort;
}

interface State extends AbstractSearchFormState {
	isAccordionOpen: boolean;
	banks: FormCheckOptions<string>;
	bankCodesLoading: boolean;
	openInvalidCollapse: string;
	showCsokPlusForms?: boolean;
	error: boolean;
}

export class MortgageSearchForm extends AbstractSearchForm<MortgageSearchParams, Props, State> {
	private bankCodesLoaded = false;
	public componentDidMount(): void {
		super.componentDidMount();
		this.loadBanks();
	}

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			isAccordionOpen: false,
			openInvalidCollapse: null,
			error: false,
			banks: [],
			bankCodesLoading: false,
			showCsokPlusForms: false,
		};
	}

	private loadBanks(calculationDate: string = dayjs().format("YYYY-MM-DD")): void {
		this.setState({
			banks: [],
			bankCodesLoading: true,
		});
		this.promisePool
			.add(getRegularBanks(calculationDate))
			.then((results) => {
				this.setState(
					{
						banks: results,
						bankCodesLoading: false,
					},
					() => {
						this.setFormValue(
							"bankCodes",
							results.map((item) => item.value),
						);
						this.bankCodesLoaded = true;
					},
				);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	private checkInvalidDetailsInputs(
		_values: MortgageSearchParams,
		form: FormApi<MortgageSearchParams>,
		isAccordionOpen: boolean,
	): void {
		if (!this.bankCodesLoaded) return;
		const { variant } = this.props;
		const { error } = this.state;
		const isCsok = isCSOKVariant(variant);
		const { errors } = form.getState();
		const mainInputFields = ["name", "loanAmount", "maturity", "income", "realEstateValue"];

		if (isCsok || variant === "TAMOGATOTT_FELUJITAS") {
			mainInputFields.push("numberOfChildren");
		}

		const hasError =
			Object.entries(errors).find((item) => {
				const fieldName = item[0];
				const isCustomField = !mainInputFields.includes(fieldName);

				if (isCustomField) {
					if (this.isCsokOrCsokPlus(variant)) {
						if (!isAccordionOpen) {
							this.setState(
								{
									isAccordionOpen: true,
								},
								() => {
									this.promisePool.addTimeout(200).then(() => {
										form.submit();
										this.openCollapseAndFocusToInvalidField(fieldName);
									});
								},
							);
						} else {
							this.openCollapseAndFocusToInvalidField(fieldName);
						}
					}
				}

				return isCustomField;
			}) !== undefined;

		if (error && !hasError) {
			this.setState({
				error: false,
			});
		}
	}

	private openCollapseAndFocusToInvalidField(fieldName: string): void {
		const { multipleInterestPeriods } = this.props;
		const { openInvalidCollapse, error } = this.state;
		let openCollapseName =
			(multipleInterestPeriods ? "interestPeriods" : "interestPeriod") === fieldName ? "interest-period" : null;

		if (["loanGoal", "realEstateLocation"].includes(fieldName)) {
			openCollapseName = "property-info";
		} else if (["age", "numberOfChildren"].includes(fieldName)) {
			openCollapseName = "family-info";
		} else if (["currentInstallment", "currentCreditLine", "monthlyDeposit"].includes(fieldName)) {
			openCollapseName = "loans-and-savings";
		} else if (["customerRating", "calculationDate"].includes(fieldName)) {
			openCollapseName = "admin";
		}

		if (openInvalidCollapse !== openCollapseName) {
			this.promisePool.addTimeout(200).then(() => {
				this.setState(
					{
						openInvalidCollapse: openCollapseName,
						error: true,
					},
					() => {
						this.focus(fieldName);
					},
				);
			});
		} else if (!error) {
			this.setState(
				{
					error: true,
				},
				() => {
					this.focus(fieldName);
				},
			);
		} else {
			this.focus(fieldName);
		}
	}

	private focus(fieldName: string): void {
		const element = document.querySelector(`.search-form .d-md-none [name="${fieldName}"]`) as HTMLInputElement;

		if (element !== null) {
			element.focus();
		}
	}

	protected renderFields(values: MortgageSearchParams, form: FormApi<MortgageSearchParams>): ReactNode {
		const { variant } = this.props;
		let collapseBlockTitle = "";

		switch (variant) {
			case "CSOK":
				collapseBlockTitle = "Részletes CSOK-hitel kalkulátor";
				break;
			case "CSOK_PLUSZ":
				collapseBlockTitle = "Részletes CSOK plusz kalkulátor";
				break;
			default:
				collapseBlockTitle = "Részletes lakáshitel kalkulátor";
		}
		const { isAccordionOpen, openInvalidCollapse, error } = this.state;
		const hasExtra = this.hasExtra();
		const loanGoals = this.getLoanGoals(values);
		this.checkInvalidDetailsInputs(values, form, isAccordionOpen);

		const isBaseVariant = variant === undefined;
		const colProps = {
			xs: 12,
			md: 6,
			xl: 3,
			lg: 3,
		};

		return (
			<>
				<Row>
					{variant === "GREEN_LOAN" && (
						<Alert variant="danger" icon={<FontAwesomeIcon icon={fasCircleExclamation} />} size="lg">
							A Zöld hitel keretösszege kimerült, a pénzintézetek felfüggesztették a kölcsön
							értékesítését. A jelenleg elérhető piaci ajánlatokat megtalálhatod{" "}
							<a href="/lakashitel-kalkulator/" target="_blank" rel="noreferrer">
								lakáshitel kalkulátorunkkal
							</a>
							!
						</Alert>
					)}
					<Col {...colProps}>{this.renderRealEstateValueCol(values)}</Col>
					<Col {...colProps}>
						<FormCurrency
							label={__("Hitelösszeg")}
							name="loanAmount"
							prepend={<FontAwesomeIcon icon={farMoneyBill1} />}
							infoTooltip={__("Mekkora hitelt szeretnél felvenni?")}
							validate={this.validateLoanAmount}
							required
						/>
					</Col>
					<Col {...colProps}>
						<FormNumber
							label={__("Futamidő")}
							name="maturity"
							prepend={<FontAwesomeIcon icon={farCalendarDays} />}
							append="év"
							infoTooltip={__("Ennyi időn keresztül fogod visszafizetni a hitelt.")}
							validate={this.validateMaturity}
							required
						/>
					</Col>
					{isBaseVariant && (
						<Col {...colProps}>
							<FormSelect
								label={__("Hitelcél")}
								name="loanGoal"
								prepend={<FontAwesomeIcon icon={farCircleDot} />}
								options={loanGoals}
								infoTooltip={__("Milyen célra szeretnéd felvenni a hitelt?")}
								onChange={(loanGoal) => this.onLoanGoalChange(loanGoal, form)}
								required
							/>
						</Col>
					)}
					<Col {...colProps}>
						<FormCurrency
							label={__("Család havi jövedelme")}
							name="income"
							prepend={<FontAwesomeIcon icon={fasUsers} />}
							infoTooltip={
								isBaseVariant
									? undefined
									: __(
											"Házas-, élettársak esetén a jövedelmeket összevontan add meg! Megadható minden hivatalosan igazolható NETTÓ jövedelem, azaz munkabér, vállalkozói jövedelem, GYED, CSED, TGYÁS, családi pótlék, lakáskiadásból származó bérleti díj, stb.",
										)
							}
							validate={this.validateIncome}
							required
						/>
					</Col>
					{this.renderFamilyInfo()}
					{this.isCsokOrCsokPlus(variant) && (
						<FormCheck
							label={__(" Megfelelek a 10% önerő előírásainak")}
							name="qualifiesForHigherLtv"
							infoTooltip={__(
								"Az igénylők egyike sem töltheti be a 41. életévét, és egyikük sem rendelkezhetett lakásban 50%-ot elérő tulajdoni hányaddal (kivéve olyan lakástulajdonnal, ami haszonélvezeti joggal terhelt).",
							)}
							className="mb-0 ml-3"
							autoReset
							disabled={!["UJ_LAKAS", "HASZNALT_LAKAS", "EPITES"].includes(values.loanGoal)}
						/>
					)}
					<FormCheck
						label={__(" Megfelelek az önkéntes kamatplafon előírásainak")}
						name="qualifiesForVoluntaryInterestRateCap"
						infoTooltip={__(
							"35 év alatti vagyok és első lakásom vásárolnám meg a kölcsönből. Az épület „A+” vagy annál kedvezőbb energetikai besorolású és a primer-energiaigénye nem haladja meg a 68 kWh/m2/év értéket. Az épület alapterülete nem haladja meg a 60 négyzetmétert. A lakás vételára nem haladja meg az 1 200 000 Ft/m2 értéket. A bankok a fenti feltételektől eltérhetnek.",
						)}
						className="mb-0 ml-3"
						autoReset
					/>
					{["TAMOGATOTT_FELUJITAS", "GREEN_LOAN", "CSOK"].includes(variant) && (
						<>
							<Col {...colProps}>{this.renderNumberOfChildrenCurrent()}</Col>
							<Col {...colProps}>{this.renderNumberOfChildrenCommitment()}</Col>
						</>
					)}
				</Row>
				{!["TAMOGATOTT_FELUJITAS", "CSOK", "CSOK_PLUSZ"].includes(variant) && (
					<Row>
						<Col md={6}>
							<div>
								{this.hasExtra() && (
									<FormCheck
										key="only-green"
										label={__("Zöld lakáshitelt szeretnél új lakáscélra?")}
										name="onlyGreen"
										className="mb-1"
										infoTooltip={__(
											'Új lakás vásárlására, építésre forditható a kölcsön. Azon lakások felelnek meg, melyeknek az energetikai besorolása eléri legalább a "BB" kategóriát. Emellett az épület primer energiaigénye nem haladhatja meg a 90 kWh/m2/év értéket.',
										)}
										onChange={(onlyGreen) => this.onOnlyGreenChange(onlyGreen, form)}
									/>
								)}
							</div>
						</Col>
						{!isBaseVariant && (
							<Col md={6} className="text-right">
								<a href="/lakashitel-kalkulator/mennyi-lakashitelt-kaphatok/?link_type=in-calculator">
									<FontAwesomeIcon icon={fasShuffle} className="mr-2" />
									<span className="text-underlined how-much-link">
										{__("Mennyi lakáshitelt vehetsz fel?")}
									</span>
								</a>
							</Col>
						)}
					</Row>
				)}
				{["TAMOGATOTT_FELUJITAS", "CSOK", "CSOK_PLUSZ"].includes(variant) && (
					<>
						<Col md={6} className="pl-0">
							<FormCheck
								label={__("Első közös lakás vásárlása, építése")}
								name="wantsFirstHouse"
								className="my-0"
							/>
						</Col>
						<Col md={6} className="pl-0">
							<FormCheck
								label={__(" Megfelelek a 10% önerő előírásainak")}
								name="qualifiesForHigherLtv"
								infoTooltip={__(
									"Az igénylők egyike sem töltheti be a 41. életévét, és egyikük sem rendelkezhetett lakásban 50%-ot elérő tulajdoni hányaddal (kivéve olyan lakástulajdonnal, ami haszonélvezeti joggal terhelt).",
								)}
								className="mb-0"
								disabled={!["UJ_LAKAS", "HASZNALT_LAKAS", "EPITES"].includes(values.loanGoal)}
							/>
						</Col>
					</>
				)}
				{!["CSOK_PLUSZ", "CSOK", "HITELKIVALTAS", "FELUJITAS", "SZAB_FEL"].includes(variant) && (
					<p className="text-center mt-3 mb-1">
						A CSOK Plusz igénylésénél fontos a gyermekek számának megadása!
					</p>
				)}
				<CollapseBlock
					title={collapseBlockTitle}
					name="details"
					variant="center"
					active={isAccordionOpen}
					onClick={this.onAdvancedClick}
				>
					<>
						<Row className="d-none d-md-flex">
							<Col xs={12}>
								{this.isCsokOrCsokPlus(variant) && this.renderInterestPeriodSelector(values, true)}
							</Col>
							{this.renderPropertyInfo(loanGoals, form)}
							{this.renderLoansAndSavings()}
							{this.renderAdminFields()}
						</Row>
						<div className="d-md-none">
							{this.isCsokOrCsokPlus(variant) && (
								<CollapseBlock
									name="interest-period"
									defaultActive={openInvalidCollapse === "interest-period"}
									disabled={error && openInvalidCollapse === "interest-period"}
									title={<b>{__("Kamatperiódus")}</b>}
								>
									{this.renderInterestPeriodSelector(values, false)}
								</CollapseBlock>
							)}
							<CollapseBlock
								name="property-info"
								defaultActive={openInvalidCollapse === "property-info"}
								disabled={error && openInvalidCollapse === "property-info"}
								title={<b>{__("Ingatlan adatai")}</b>}
							>
								{this.renderPropertyInfo(loanGoals, form)}
							</CollapseBlock>
							<CollapseBlock
								name="family-info"
								defaultActive={openInvalidCollapse === "family-info"}
								disabled={error && openInvalidCollapse === "family-info"}
								title={<b>{__("Család adatai")}</b>}
							>
								{this.renderFamilyInfo()}
							</CollapseBlock>
							<CollapseBlock
								name="loans-and-savings"
								defaultActive={openInvalidCollapse === "loans-and-savings"}
								disabled={error && openInvalidCollapse === "loans-and-savings"}
								title={<b>{__("Hitelek és megtakarítások")}</b>}
							>
								{this.renderLoansAndSavings()}
							</CollapseBlock>
							{hasExtra && (
								<CollapseBlock
									name="admin"
									defaultActive={openInvalidCollapse === "admin"}
									disabled={error && openInvalidCollapse === "admin"}
									title={<b>{__("Egyéb beállítások")}</b>}
								>
									{this.renderAdminFields()}
								</CollapseBlock>
							)}
						</div>

						{this.renderTermsOfCondition()}
						{this.isCsokOrCsokPlus(variant) && this.renderOtherSettings(values, form)}
						{hasExtra && this.renderBankGroup()}
					</>
				</CollapseBlock>
			</>
		);
	}

	private renderTermsOfCondition(): ReactNode {
		const { variant } = this.props;
		const className = "mb-0";

		return (
			<CollapseBlock name="conditions" title={<b>{__("Vállalt feltételek")}</b>}>
				<Row className="mb-3">
					<Col md={6}>
						<FormCheck
							label={__("Jövedelem, egyéb jóváírás bankhoz irányítása")}
							name="receiptOfIncome"
							infoTooltip={__(
								"Úgy számolunk, hogy a család havi jövedelméhez beírt teljes összeget a bankhoz utalod.",
							)}
							className={className}
						/>
					</Col>
					<Col md={6}>
						<FormCheck
							label={__("Lakásbiztosítás")}
							name="definedInsuranceUsage"
							infoTooltip={__("A bank által meghatározott lakásbiztosítás megkötése.")}
							className={className}
						/>
					</Col>

					<Col md={6}>
						<FormCheck
							label={__("Aktív számlahasználat")}
							name="activeAccountPackageUsage"
							infoTooltip={__(
								"A kölcsön törlesztési számláján csoportos beszedési megbízás vagy egyéb elvárt tranzakciók teljesülnek.",
							)}
							className={className}
						/>
					</Col>
					<Col md={6}>
						<FormCheck
							label={__("Hitelfedezeti biztosítás")}
							name="loanCoverageInsurance"
							infoTooltip={__(
								"Egyes lakáshiteleket alacsonyabb kamattal vagy költséggel vehetsz fel, amennyiben például munkanélküliségre, keresőképtelenségre szóló biztosítást kötsz a banknál.",
							)}
							className={className}
						/>
					</Col>

					<Col md={6}>
						<FormCheck
							label={__("Aktív kártyahasználat")}
							name="activeCreditCardUsage"
							infoTooltip={__("Bankkártyás vásárlás havonta a bank által elvárt összegben.")}
							className={className}
						/>
					</Col>
					<Col md={6}>
						<FormCheck
							label={__("Számlavezetés")}
							name="definedAccountPackageUsage"
							infoTooltip={__("A bank által meghatározott számlacsomag fenntartása.")}
							className={className}
						/>
					</Col>
					{!this.isCsokOrCsokPlus(variant) && (
						<Col md={6}>
							<FormCheck
								label={__("Zöld lakáshitel ajánlatok")}
								name="withEnergyEfficient"
								className="mb-1"
							/>
						</Col>
					)}
				</Row>
			</CollapseBlock>
		);
	}

	private renderOtherSettings(values: MortgageSearchParams, form: FormApi<MortgageSearchParams>): ReactNode {
		const { highlightedBank, variant } = this.props;
		const className = "mb-0";

		return (
			<CollapseBlock name="otherSettings" title={<b>{__("CSOK és egyéb beállítások")}</b>}>
				<div className="mb-3">
					<Row>
						<Col md={6}>
							<FormCheck
								label={__("Első közös lakás vásárlása, építése")}
								name="wantsFirstHouse"
								className={className}
							/>
							<FormCheck
								label={__("Jogosult vagyok falusi CSOK-ra és CSOK hitelre")}
								name="eligibleForFalusiCsok"
								className={className}
								onChange={(eligibleForFalusiCsok) =>
									this.onEligibleCsokChange(eligibleForFalusiCsok, form, values)
								}
							/>
							<FormCheck
								label={__("Jogosult vagyok CSOK PLUSZ hitelre")}
								name="eligibleForCsokPlus"
								onChange={(eligibleForCsokPlus) =>
									this.onEligibleCsokPlusChange(eligibleForCsokPlus, form, values)
								}
								className={className}
							/>
							<FormCheck
								label={__("Csak a CSOK hiteleket szeretném látni")}
								name="csok"
								infoTooltip={__(
									"A CSOK hitel kizárólag új lakás vásárlásra, építésre és használt lakás vásárlásra igényelt CSOK mellet kérhető. A CSOK hitel a futamidő végéig fix, 3%-os kamatozású. Maximális összege két gyermek (meglévő vagy vállalt) esetén 10 millió, míg 3 gyermek (meglévő vagy vállalt) esetében 15 millió forint.",
								)}
								className={className}
								disabled={
									!CSOK_LOAN_GOALS.includes(values.loanGoal) ||
									values.onlyOfk ||
									values.onlyCsokPlus ||
									!values.eligibleForFalusiCsok
								}
								onChange={(csok) => this.onCsokChange(csok, form)}
								autoReset
							/>
							<FormCheck
								label={__("Csak a CSOK Plusz hiteleket szeretném látni")}
								name="onlyCsokPlus"
								infoTooltip={__(
									"A CSOK PLUSZ hitel kizárólag új lakás vásárlásra, építésre, használat lakás vásárlásra és bővítésre igényelhető. A CSOK PLUSZ hitel a futamidő végéig fix, 3%-os kamatozású. Maximális összege 1 gyermek (csak vállalt gyermek számít) esetén 15 millió, 2 gyermek (meglévő + minimum 1 vállalt)  esetén 30 millió, 3 gyermek (meglévő + minimum 1 vállalt) esetén 50 millió Ft.",
								)}
								onChange={(onlyCsokPlus) => this.onCsokPlusChange(onlyCsokPlus, form)}
								className={className}
								disabled={
									!CSOK_PLUS_LOAN_GOALS.includes(values.loanGoal) ||
									values.csok ||
									!values.eligibleForCsokPlus
								}
								autoReset
							/>
						</Col>
						<Col md={6}>
							<FormCheck
								label={__("Csak a Minősített Fogyasztóbarát Lakáshiteleket szeretném látni")}
								name="onlyFbl"
								className={className}
								disabled={values.csok}
							/>
							<FormCheck
								label={__("A közszféra munkavállalóinak nyújtott kedvezményeket is szeretném látni")}
								name="publicSectorToo"
								className={className}
							/>
							{this.isCsokOrCsokPlus(variant) && (
								<FormCheck
									label={__("Zöld lakáshitel ajánlatok")}
									name="withEnergyEfficient"
									className="mb-1"
								/>
							)}
							{this.isCsokOrCsokPlus(variant) && !highlightedBank && (
								<FormCheck
									label={__("Bankonként és kamatperiódusonként 1 ajánlat")}
									name="onePerBank"
									className="mb-1"
								/>
							)}
						</Col>
					</Row>
				</div>
			</CollapseBlock>
		);
	}

	private renderRealEstateValueCol(values: MortgageSearchParams): ReactNode {
		const { variant } = this.props;

		return (
			<FormCurrency
				label={__("Ingatlan értéke")}
				name="realEstateValue"
				placeholder={
					variant === undefined && values.loanAmount
						? formatText(Math.min(CONFIG.MAX_REAL_ESTATE_VALUE, values.loanAmount * 2), "ft").replace(
								" Ft",
								"",
							)
						: ""
				}
				prepend={<FontAwesomeIcon icon={fasHouse} />}
				infoTooltip={__("A hitel fedezetéül szolgáló ingatlan piaci értéke.")}
				validate={this.validateRealEstateValue}
			/>
		);
	}

	private renderBankGroup(): ReactNode {
		const { banks, bankCodesLoading } = this.state;

		return (
			<div className="mb-3">
				<div className="form-label">{__("Melyik bank ajánlatait szeretnéd látni?")}</div>
				{bankCodesLoading && <p>Banklista betöltése folyamatban...</p>}
				<FormCheckList
					name="bankCodes"
					selectAllOption
					optionClassName="col-lg-4 col-md-6 col-12"
					options={banks}
					required
				/>
			</div>
		);
	}

	private renderNumberOfChildrenCurrent(): ReactNode {
		return (
			<FormNumber
				label={__("Meglévő gyermekek")}
				name="numberOfChildrenCurrent"
				prepend={<FontAwesomeIcon icon={fasChild} />}
				append="fő"
				validate={this.validateNumberOfChildrenCurrent}
				required
			/>
		);
	}

	private renderNumberOfChildrenCommitment(): ReactNode {
		return (
			<FormNumber
				label={__("Vállalt gyermekek")}
				name="numberOfChildrenCommitment"
				prepend={<FontAwesomeIcon icon={fasChild} />}
				append="fő"
				infoTooltip={__(
					"CSOK PLUSZ hitel esetén minimum 1 vállalt gyermek szükséges. A 12 hetes magzat még vállaltnak minősül.",
				)}
				validate={this.validateNumberOfChildrenCommitment}
				required
			/>
		);
	}
	private renderInterestPeriodSelector(values: MortgageSearchParams, primary: boolean): ReactNode {
		const { multipleInterestPeriods } = this.props;
		const hasExtra = this.hasExtra();
		const options = compact([
			hasExtra && { label: `3 ${__("hónap")}`, value: "THREE_MONTHS" },
			hasExtra && { label: `6 ${__("hónap")}`, value: "SIX_MONTHS" },
			hasExtra && { label: `1 ${__("év")}`, value: "ONE_YEAR" },
			hasExtra && { label: `3 ${__("év")}`, value: "THREE_YEARS" },
			!hasExtra && {
				label: __("Változó kamat"),
				value: "THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS",
			},
			{ label: `5 ${__("év")}`, value: "FIVE_YEARS" },
			{ label: `10 ${__("év")}`, value: "TEN_YEARS" },
			hasExtra && { label: `15 ${__("év")}`, value: "FIFTEEN_YEARS" },
			hasExtra && { label: `20 ${__("év")}`, value: "TWENTY_YEARS" },
			hasExtra && { label: `25 ${__("év")}`, value: "TWENTY_FIVE_YEARS" },
			hasExtra && { label: `30 ${__("év")}`, value: "THIRTY_YEARS" },
			!hasExtra && {
				label: `15-30 ${__("év")}`,
				value: "FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS",
			},
			{
				label: __("Végig fix*"),
				value: "FULL",
			},
		]);

		if (multipleInterestPeriods) {
			return (
				<FormCheckList
					label={
						<>
							{__("Kamatperiódus")} <small className="text-muted">({__("fix törlesztőrészlet")})</small>
						</>
					}
					name="interestPeriods"
					className="interestPeriods"
					infoTooltip={__(
						"Mennyi ideig legyen változatlan a törlesztőrészlet? (Ezt nevezik kamatperiódusnak. Ha például 1 évet választasz, akkor évente fog változni a hiteled kamata. Emelkedhet és csökkenhet is.)",
					)}
					options={options}
					optionClassName="text-nowrap"
					info={
						<div className="periodus-extra-info">
							{__(
								"*Amennyiben a hitel futamideje nem hosszabb mint a kamatperiódusa, akkor a végig fix– kockázatmentes kategóriába soroljuk.",
							)}
						</div>
					}
					disabled={values.csok || values.onlyCsokPlus}
					required
				/>
			);
		}

		return (
			<FormRadio
				label={
					<>
						{__("Kamatperiódus")} <small className="text-muted">({__("fix törlesztőrészlet")})</small>
					</>
				}
				name={primary ? "interestPeriod" : "interestPeriod1"}
				className="interestPeriods"
				infoTooltip={__(
					"Mennyi ideig legyen változatlan a törlesztőrészlet? (Ezt nevezik kamatperiódusnak. Ha például 1 évet választasz, akkor évente fog változni a hiteled kamata. Emelkedhet és csökkenhet is.)",
				)}
				options={options}
				info={
					<div className="periodus-extra-info">
						{__(
							"*Amennyiben a hitel futamideje nem hosszabb mint a kamatperiódusa, akkor a végig fix– kockázatmentes kategóriába soroljuk.",
						)}
					</div>
				}
				disabled={values.csok || values.onlyCsokPlus}
				onChange={(newValue) => {
					this.setFormValue(primary ? "interestPeriod1" : "interestPeriod", newValue);
				}}
				inlineOptions
				required
			/>
		);
	}

	private renderPropertyInfo(loanGoals: LoanGoalOption[], form: FormApi<MortgageSearchParams>): ReactNode {
		const { variant } = this.props;
		const renderProps = {
			xl: 3,
			lg: 4,
			md: 6,
			className: "align-self-end",
		};
		const isBaseVariant = variant === undefined;

		return (
			<>
				{!isBaseVariant && (
					<Col {...renderProps}>
						<FormSelect
							label={__("Hitelcél")}
							name="loanGoal"
							prepend={<FontAwesomeIcon icon={farCircleDot} />}
							options={loanGoals}
							infoTooltip={__(
								"Válaszd ki milyen célra szeretnéd felvenni a hitelt. CSOK Plusz esetén csak a jogszabályban megadott hitelcélokra igényelhető.",
							)}
							onChange={(loanGoal) => this.onLoanGoalChange(loanGoal, form)}
							disabled={["HITELKIVALTAS", "EPITES", "FELUJITAS", "TAMOGATOTT_FELUJITAS"].includes(
								variant,
							)}
							required
						/>
					</Col>
				)}
				<Col {...renderProps}>
					<FormSelect
						label={__("Ingatlan elhelyezkedése")}
						name="realEstateLocation"
						prepend={<FontAwesomeIcon icon={fasLocationDot} />}
						validate={this.validateRealEstateLocation}
						options={[
							{ label: __("mindegy"), value: "" },
							{ label: __("Budapest vagy megyei jogú"), value: "BP_COUNTY_CITY" },
							{ label: __("Egyéb település"), value: "OTHER_CITY" },
						]}
						infoTooltip={__(
							"Budapesten és megyei jogú városokban csak a CSOK PLUSZ hitel érhető el, a Falusi CSOK és CSOK hitel nem.",
						)}
					/>
				</Col>
			</>
		);
	}

	private renderFamilyInfo(): ReactNode {
		const { variant } = this.props;
		const renderProps = {
			xl: 3,
			lg: 4,
			md: 6,
		};

		return (
			<>
				{!isCSOKVariant(variant) && !["TAMOGATOTT_FELUJITAS", "GREEN_LOAN"].includes(variant) && (
					<>
						<Col {...renderProps}>{this.renderNumberOfChildrenCurrent()}</Col>
						<Col {...renderProps}>{this.renderNumberOfChildrenCommitment()}</Col>
					</>
				)}
			</>
		);
	}

	private renderLoansAndSavings(): ReactNode {
		const renderProps = {
			xl: 3,
			lg: 4,
			md: 6,
			className: "align-self-end",
		};

		return (
			<>
				<Col {...renderProps}>
					<FormCurrency
						label={__("Meglévő hiteltörlesztések")}
						name="currentInstallment"
						prepend={<FontAwesomeIcon icon={fasCoins} />}
						infoTooltip={__(
							"A fennálló hitelek (pl.: lakáshitel, személyi kölcsön, stb.) törlesztőrészleteinek az összege.",
						)}
						validate={this.validateCurrentInstallment}
						required
					/>
				</Col>
				<Col {...renderProps}>
					<FormCurrency
						label={__("Meglévő hitelkeretek")}
						name="currentCreditLine"
						prepend={<FontAwesomeIcon icon={farCreditCard} />}
						infoTooltip={__(
							"A hitelkártya és folyószámlahitel hitelkereteinek teljes összege (nem csak a kihasznált).",
						)}
						validate={this.validateCurrentCreditLine}
						required
					/>
				</Col>
				<Col {...renderProps}>
					<FormCurrency
						label={__("Havi rendszeres megtakarítás")}
						name="monthlyDeposit"
						prepend={<FontAwesomeIcon icon={farCalendarPlus} />}
						infoTooltip={__(
							"A hiteltörlesztésen és egyéb havi kiadásokon felül rendszeresen vállalható havi megtakarítás, amelyet nem költesz el másra.",
						)}
						validate={this.validateMonthlyDeposit}
						required
					/>
				</Col>
			</>
		);
	}

	private renderAdminFields(): ReactNode {
		if (!this.hasExtra()) {
			return null;
		}
		const renderProps = {
			xl: 3,
			lg: 4,
			md: 6,
			className: "align-self-end",
		};

		return (
			<>
				<Col {...renderProps}>
					<FormSelect<number>
						className="admin-only"
						label={
							<>
								<FontAwesomeIcon icon={farLock} />
								{__("Ügyfélminősítés")}
							</>
						}
						name="customerRating"
						prepend={<FontAwesomeIcon icon={fasStar} />}
						options={[
							{ label: __("automatikus"), value: undefined },
							{ label: "1", value: 1 },
							{ label: "2", value: 2 },
							{ label: "3", value: 3 },
							{ label: "4", value: 4 },
							{ label: "5", value: 5 },
							{ label: "6", value: 6 },
						]}
					/>
				</Col>

				<Col {...renderProps}>
					<FormDate
						className="admin-only"
						label={
							<>
								<FontAwesomeIcon icon={farLock} />
								{__("Kalkuláció időpontja")}
							</>
						}
						name="calculationDate"
						onBlur={(date) => this.loadBanks(date)}
						required
					/>
				</Col>
			</>
		);
	}

	@autobind
	private validateLoanAmount(loanAmount: number, values: MortgageSearchParams): string {
		if (isNaN(loanAmount)) {
			return __("A hitelösszeg megadása kötelező!");
		}

		if (loanAmount < CONFIG.MIN_LOAN_AMOUNT) {
			return `${__("A minimálisan felvehető hitelösszeg")} ${formatText(CONFIG.MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > CONFIG.MAX_LOAN_AMOUNT) {
			return `${__("A maximálisan felvehető hitelösszeg")} ${formatText(CONFIG.MAX_LOAN_AMOUNT, "ft")}!`;
		}

		if (values.onlyOfk) {
			if (loanAmount > CONFIG.MAX_OFK_LOAN_AMOUNT) {
				return `${__("A maximálisan felvehető hitelösszeg")} ${formatText(CONFIG.MAX_OFK_LOAN_AMOUNT, "ft")}!`;
			}
		}

		if (values.qualifiesForHigherLtv) {
			const ltv = values.qualifiesForHigherLtv ? 0.9 : 0.8;

			if (["EPITES", "HASZNALT_LAKAS", "UJ_LAKAS"].includes(values.loanGoal)) {
				if (loanAmount > ltv * values.realEstateValue) {
					return `A hitelösszeg nem lehet nagyobb az ingatlan értékének ${ltv * 100}%-ánál (${formatText(
						values.realEstateValue * ltv,
						"ft",
					)}).`;
				}
			}
		} else if (loanAmount > 0.8 * values.realEstateValue) {
			return `A hitelösszeg nem lehet nagyobb az ingatlan értékének 80%-ánál (${formatText(
				values.realEstateValue * 0.8,
				"ft",
			)}).`;
		}

		if (values.csok) {
			if (
				values.numberOfChildrenCommitment + values.numberOfChildrenCurrent < 3 &&
				loanAmount > CONFIG.MAX_CSOK_LOAN_AMOUNT_FOR_ONE_OR_TWO_CHILDREN
			) {
				return `${__("A CSOK-hitel maximális összege két gyermek esetén (meglévő vagy vállalt)")} ${formatText(
					CONFIG.MAX_CSOK_LOAN_AMOUNT_FOR_ONE_OR_TWO_CHILDREN,
					"ft",
				)}.`;
			}

			if (
				values.numberOfChildrenCommitment + values.numberOfChildrenCurrent >= 3 &&
				loanAmount > CONFIG.MAX_CSOK_LOAN_AMOUNT
			) {
				return `${__(
					"A CSOK-hitel maximális összege három gyermek esetén (meglévő vagy vállalt)",
				)} ${formatText(CONFIG.MAX_CSOK_LOAN_AMOUNT, "ft")}.`;
			}
		}

		if (values.onlyCsokPlus) {
			const numberOfChildrenTotal = values.numberOfChildrenCommitment + values.numberOfChildrenCurrent;

			if (
				values.numberOfChildrenCommitment <= 1 &&
				values.numberOfChildrenCurrent === 0 &&
				loanAmount > CONFIG.MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_ONE_CHILDREN
			) {
				return `${__(
					"A CSOK PLUSZ hitel maximális összege 1 gyermek (csak vállalt számít) esetén",
				)} ${formatText(CONFIG.MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_ONE_CHILDREN, "ft")}.`;
			}

			if (
				numberOfChildrenTotal === 2 &&
				values.numberOfChildrenCommitment > 0 &&
				loanAmount > CONFIG.MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_TWO_CHILDREN
			) {
				return `${__(
					"A CSOK PLUSZ hitel maximális összege 2 gyermek (meglevő + minimum 1 vállalt) esetén",
				)} ${formatText(CONFIG.MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_TWO_CHILDREN, "ft")}.`;
			}

			if (
				numberOfChildrenTotal >= 3 &&
				values.numberOfChildrenCommitment > 0 &&
				loanAmount > CONFIG.MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_THREE_CHILDREN
			) {
				return `${__(
					"A CSOK PLUSZ hitel maximális összege 3 gyermek (meglevő + minimum 1 vállalt)",
				)} ${formatText(CONFIG.MAX_CSOK_PLUSZ_LOAN_AMOUNT_FOR_THREE_CHILDREN, "ft")}.`;
			}

			if (!values.wantsFirstHouse && values.realEstateValue * 0.8 < loanAmount && !values.qualifiesForHigherLtv) {
				return `${__("A hitelösszeg nem lehet nagyobb az ingatlan értékének a 80%-nál, azaz ")} ${formatText(
					values.realEstateValue * 0.8,
					"ft",
				)}-nál. Az első lakást vásárlóknak bizonyos esetekben ez az érték 90% is lehet.`;
			}
		}
	}

	@autobind
	private validateMaturity(maturity: number, values: MortgageSearchParams): string {
		if (isNaN(maturity)) {
			return __("A futamidő megadása kötelező!");
		}

		if (maturity < CONFIG.MIN_MATURITY) {
			return `${__("A minimálisan megadható futamidő")} ${CONFIG.MIN_MATURITY} ${__("év!")}`;
		}

		if (maturity > CONFIG.MAX_MATURITY) {
			return `${__("A maximálisan megadható futamidő")} ${CONFIG.MAX_MATURITY} ${__("év!")}`;
		}

		// CSOK
		if (values.csok && maturity > CONFIG.MAX_CSOK_MATURITY) {
			return `${__("A CSOK-hitel futamideje maximum")} ${CONFIG.MAX_CSOK_MATURITY} ${__("év lehet!")}`;
		}

		// OFK
		if (values.onlyOfk && maturity > CONFIG.MAX_OFK_MATURITY) {
			return `${__("Lakásfelújítási hitel maximum")} ${CONFIG.MAX_OFK_MATURITY} ${__(
				"éves futamidőre vehető fel!",
			)}`;
		}
	}

	@autobind
	private validateRealEstateValue(realEstateValue: number, values: MortgageSearchParams): string {
		if (realEstateValue < CONFIG.MIN_REAL_ESTATE_VALUE || !realEstateValue) {
			return `${__("A vásárolni kívánt ingatlan minimális értéke")} ${formatText(
				CONFIG.MIN_REAL_ESTATE_VALUE,
				"ft",
			)}!`;
		}

		if (realEstateValue > CONFIG.MAX_REAL_ESTATE_VALUE) {
			return `${__("A vásárolni kívánt ingatlan maximális értéke")} ${formatText(
				CONFIG.MAX_REAL_ESTATE_VALUE,
				"ft",
			)}!`;
		}

		if (values.onlyCsokPlus && values.wantsFirstHouse && realEstateValue > 80_000_000) {
			return `${__("Az ingaltan értéke nem lehet több mint ")} ${formatText(80_000_000, "ft")}!`;
		}

		if (values.onlyCsokPlus && !values.wantsFirstHouse && realEstateValue > 150_000_000) {
			return `${__("Az ingaltan értéke nem lehet több mint ")} ${formatText(150_000_000, "ft")}!`;
		}
	}

	@autobind
	private validateRealEstateLocation(realEstateLocation: string, values: MortgageSearchParams): string {
		if (realEstateLocation === "BP_COUNTY_CITY" && values.csok) {
			return "Budapest  és megyei jogú városban falusi CSOK-hoz kapcsolódó CSOK hitel nem igényelhető.";
		}
	}

	@autobind
	private validateIncome(income: number): string {
		if (isNaN(income) || !income) {
			return __("A jövedelem megadása kötelező!");
		}

		if (income < Config.getMinWage()) {
			return sprintf(
				__("A megadható minimális összjövedelem %s, amely megegyezik az aktuális minimálbérrel."),
				formatText(Config.getMinWage(), "ft"),
			);
		}

		if (income > CONFIG.INCOME_MAX) {
			return `${__("A megadható maximális jövedelem")} ${formatText(CONFIG.INCOME_MAX, "ft")}!`;
		}
	}

	@autobind
	private validateNumberOfChildrenCommitment(
		numberOfChildrenCommitment: number,
		values: MortgageSearchParams,
	): string {
		if (isNaN(numberOfChildrenCommitment)) {
			return __("A gyermekek számának megadása kötelező!");
		}

		if (isNaN(numberOfChildrenCommitment) || numberOfChildrenCommitment < 0) {
			return __("Érvénytelen érték!");
		}

		if (numberOfChildrenCommitment > CONFIG.MAX_CHILDREN) {
			return `${__("A maximális megadható gyermekszám")} ${CONFIG.MAX_CHILDREN}!`;
		}

		if (values.csok && values.numberOfChildrenCurrent + numberOfChildrenCommitment < 2) {
			return __("CSOK-hitelt minimum két gyermek esetén (meglévő vagy vállalt) vehetsz fel.");
		}

		if (values.onlyCsokPlus && numberOfChildrenCommitment < 1) {
			return __(
				"CSOK PLUSZ hitel esetén minimum 1 vállalt gyermek szükséges. A 12 hetes magzat még vállaltnak minősül.",
			);
		}
	}

	@autobind
	private validateNumberOfChildrenCurrent(numberOfChildrenCurrent: number, values: MortgageSearchParams): string {
		if (isNaN(numberOfChildrenCurrent)) {
			return __("A gyermekek számának megadása kötelező!");
		}

		if (isNaN(numberOfChildrenCurrent) || numberOfChildrenCurrent < 0) {
			return __("Érvénytelen érték!");
		}

		if (numberOfChildrenCurrent > CONFIG.MAX_CHILDREN) {
			return `${__("A maximális megadható gyermekszám")} ${CONFIG.MAX_CHILDREN}!`;
		}

		if (values.csok && numberOfChildrenCurrent + values.numberOfChildrenCommitment < 2) {
			return __("CSOK-hitelt minimum két gyermek esetén (meglévő vagy vállalt) vehetsz fel.");
		}

		if (values.onlyOfk && numberOfChildrenCurrent < 1) {
			return __(
				"Otthonfelújítási kölcsönt kizárólag legalább 1 meglévő gyermek (vagy 12 hetes magzat) esetén igényelhetsz.",
			);
		}
	}

	@autobind
	private validateCurrentInstallment(currentInstallment: number): string {
		if (currentInstallment < 0) {
			return __("A hiteltörlesztő értéke nem lehet negatív!");
		}

		if (currentInstallment > CONFIG.MAX_CURRENT_INSTALLMENT) {
			return `${__("A hiteltörlesztő nem lehet nagyobb mint")} ${formatText(
				CONFIG.MAX_CURRENT_INSTALLMENT,
				"ft",
			)}!`;
		}
	}

	@autobind
	private validateCurrentCreditLine(currentCreditLine: number): string {
		if (currentCreditLine < 0) {
			return __("A meglévő hitelkeret értéke nem lehet negatív!");
		}

		if (currentCreditLine > CONFIG.MAX_CURRENT_CREDIT_LINE) {
			return `${__("A meglévő hitelkeret nem lehet nagyobb")} ${formatText(
				CONFIG.MAX_CURRENT_CREDIT_LINE,
				"ft",
			)}!`;
		}
	}

	@autobind
	private validateMonthlyDeposit(monthlyDeposit: number): string {
		if (monthlyDeposit < 0) {
			return __("A havi megtakarítás értéke nem lehet negatív!");
		}

		if (monthlyDeposit > CONFIG.MAX_MONTHLY_DEPOSIT) {
			return `${__("A havi megtakarítás nem lehet nagyobb")} ${formatText(CONFIG.MAX_MONTHLY_DEPOSIT, "ft")}!`;
		}
	}

	@autobind
	private onLoanGoalChange(loanGoal: LoanGoal, form: FormApi<MortgageSearchParams>): void {
		if (!CSOK_LOAN_GOALS.includes(loanGoal)) {
			form.change("csok", false);
		}

		if (!SOCIAL_DISCOUNT_LOAN_GOALS.includes(loanGoal)) {
			form.change("socialDiscount", false);
		}
	}

	@autobind
	private onEligibleCsokChange(
		eligibleForCsok: boolean,
		form: FormApi<MortgageSearchParams>,
		values: MortgageSearchParams,
	): void {
		if (!eligibleForCsok && values.csok) {
			form.change("csok", false);
		}
	}

	@autobind
	private onEligibleCsokPlusChange(
		eligibleForCsokPlus: boolean,
		form: FormApi<MortgageSearchParams>,
		values: MortgageSearchParams,
	): void {
		if (!eligibleForCsokPlus && values.onlyCsokPlus) {
			form.change("onlyCsokPlus", false);
		}
	}

	@autobind
	private onCsokChange(csok: boolean, form: FormApi<MortgageSearchParams>): void {
		if (csok) {
			form.change("interestPeriods", ["FIVE_YEARS"]);
			form.change("interestPeriod", undefined);
			form.change("onlyFbl", false);
			form.change("onlyOfk", false);
		} else {
			form.change("interestPeriods", [
				"FIVE_YEARS",
				"TEN_YEARS",
				"FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS",
				"FULL",
			]);
			form.change("interestPeriod", undefined);
		}
	}

	@autobind
	private onCsokPlusChange(onlyCsokPlus: boolean, form: FormApi<MortgageSearchParams>): void {
		if (onlyCsokPlus) {
			form.change("interestPeriods", ["FIVE_YEARS"]);
			form.change("interestPeriod", undefined);
		} else {
			form.change("interestPeriods", [
				"FIVE_YEARS",
				"TEN_YEARS",
				"FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS",
				"FULL",
			]);
			form.change("interestPeriod", undefined);
		}
	}

	@autobind
	private onOfkChange(onlyOfk: boolean, form: FormApi<MortgageSearchParams>): void {
		if (onlyOfk) {
			form.change("csok", false);
		}
	}

	@autobind
	private onAdvancedClick(): void {
		const { isAccordionOpen } = this.state;

		this.setState({
			isAccordionOpen: !isAccordionOpen,
		});

		this.sendEvent("BM_advancedCalculation", { action: isAccordionOpen ? "close" : "open" });
	}

	private hasExtra(): boolean {
		return Config.getCurrentUser().hasRole("MORTGAGE");
	}

	private onOnlyGreenChange(onlyGreen: boolean, form: FormApi<MortgageSearchParams>): void {
		if (onlyGreen) {
			form.change("loanGoal", "UJ_LAKAS");
		}
	}

	private getLoanGoals(values: MortgageSearchParams): LoanGoalOption[] {
		const { variant } = this.props;
		const { onlyGreen } = values;

		if (onlyGreen) {
			return LOAN_GOALS.filter((loanGoal: LoanGoalOption) => GREEN_LOAN_GOALS.includes(loanGoal.value));
		}

		if (isCSOKVariant(variant)) {
			return LOAN_GOALS.filter((loanGoal: LoanGoalOption) => CSOK_LOAN_GOALS.includes(loanGoal.value));
		}

		if (variant === "CSOK_PLUSZ") {
			return LOAN_GOALS.filter((loanGoal: LoanGoalOption) => CSOK_PLUS_LOAN_GOALS.includes(loanGoal.value));
		}

		return LOAN_GOALS;
	}

	private isCsokOrCsokPlus(variant: string): boolean {
		return !["CSOK", "CSOK_PLUSZ"].includes(variant);
	}

	protected getComponentName(): string {
		return "MortgageSearchForm";
	}
}
